import React, { useState, useEffect, useCallback, useRef } from 'react';
import TagManager from 'react-gtm-module';
import SearchInput from '../SearchInput/SearchInput';
import {
    BuildingWithConvertedAddresses,
    Comment,
    LatLng,
    OffMarketToastMessage,
    OffMarketWillingnessEvent as OffMarketWillingnessEventType,
} from '../utils/types';
import SignupLoginModal from '../SignupLoginModal/SignupLoginModal';
import BuildingMap from '../HouseMap/BuildingMap';
import MapIcon from './MapIcon';
import LoadingSpinner from '../common/LoadingSpinner';
import { getCookie } from '../utils/utils';
import { Trans, useTranslation } from 'react-i18next';
import BuildingModalGlobal from '../BuildingModal/BuildingModalGlobal';
import MessagesModal from '../MessagesModal/MessagesModal';
import ExploreListings from '../ExploreListings/ExploreListings';
import './LandingPage.scss';
import OffMarketSellIcon from '../FeedEvents/OffMarketSellIcon';
import OffMarketBuyIcon from '../FeedEvents/OffMarketBuyIcon';
import MessagesIcon from '../NavbarMessages/MessagesIcon';
import UserIcon from '../NavbarSignupLogin/UserIcon';
import { CloseIcon } from '../common/CloseIcon';
import CouldBuyOrCouldSellModal from '../BuildingModal/CouldBuyOrCouldSellModal';
import LandingPageFeedEvents from '../FeedEvents/LandingPageFeedEvents';
import LandingPagePropertyMap from '../PropertyMap/LandingPagePropertyMap';
import '../../App.scss';
import './LandingPage.scss';
import '../FeedEvents/FeedComments.scss';

type LandingPageProps = {
    showBuildingModal: boolean;
    showExploreListings: boolean;
    messagesModalVisible: boolean;
    newMessagesCount: number;
    isLoadingMore: boolean;
    isRefreshingFeed: boolean;
    conversations: any[];
    initialMapZoom: number;
    selectedBuilding: BuildingWithConvertedAddresses | null;
    feedComments: Comment[];
    userEmail: string | null;
    setFeedComments: (comments: Comment[]) => void;
    setSelectedBuilding: (building: BuildingWithConvertedAddresses | null) => void;
    setShowBuildingModal: (show: boolean) => void;
    setShowExploreListings: (show: boolean) => void;
    toggleMessagesModal: () => void;
    setFeedViewActive: (show: boolean) => void;
    handleShowExploreListings: () => void;
    handleSelectBuildingFromExplore: (buildingId: string) => void;
    handleFeedCommentClick: (comment: Comment | OffMarketWillingnessEventType) => void;
    handleRefreshFeedClick: () => void;
    handleLoadMoreFeed: () => void;
    toggleShowBuildingModal: () => void;
    fetchConversations: () => void;
    selectBuilding: (building: BuildingWithConvertedAddresses) => void;
    handleLoginSuccess: (expiresIn: number) => void;
    setLoginModalVisible: (show: boolean) => void;
    loginModalVisible: boolean;
    setTriggeredFromSearch: (triggered: boolean) => void;
    mapCenter: LatLng;
    setMapCenter: (center: LatLng) => void;
    isLoggedIn: boolean;
    setIsLoggedIn: (loggedIn: boolean) => void;
    showFilters: boolean;
    setShowFilters: (show: boolean) => void;
    triggeredFromSearch: boolean;
    showFeed: boolean;
    setShowFeed: (show: boolean) => void;
    showImageModal: string | null;
    setShowImageModal: (image: string | null) => void;
    countryOfFeedContent: string | null;
    setCountryOfFeedContent: (country: string | null) => void;
    selectedConversation: any;
    setSelectedConversation: (conversation: any) => void;
    lastMapBoundaryBoxCenter: LatLng | null;
    setLastMapBoundaryBoxCenter: (center: LatLng | null) => void;
    lastZoomLevel: number | null;
    setLastZoomLevel: (zoom: number | null) => void;
    showRemoveListingDialog: string | null;
    setShowRemoveListingDialog: (listingId: string | null) => void;
    sharedEventId: string | null;
    setSharedEventId: (eventId: string | null) => void;
};

const LandingPage = ({
    showBuildingModal,
    showExploreListings,
    messagesModalVisible,
    newMessagesCount,
    isLoadingMore,
    isRefreshingFeed,
    conversations,
    initialMapZoom,
    selectedBuilding,
    feedComments,
    userEmail,
    setFeedComments,
    setSelectedBuilding,
    setShowBuildingModal,
    setShowExploreListings,
    toggleMessagesModal,
    setFeedViewActive,
    handleShowExploreListings,
    handleSelectBuildingFromExplore,
    handleFeedCommentClick,
    handleRefreshFeedClick,
    handleLoadMoreFeed,
    toggleShowBuildingModal,
    fetchConversations,
    selectBuilding,
    handleLoginSuccess,
    setLoginModalVisible,
    loginModalVisible,
    setTriggeredFromSearch,
    mapCenter,
    setMapCenter,
    isLoggedIn,
    setIsLoggedIn,
    showFilters,
    setShowFilters,
    triggeredFromSearch,
    showFeed,
    setShowFeed,
    showImageModal,
    setShowImageModal,
    countryOfFeedContent,
    setCountryOfFeedContent,
    selectedConversation,
    setSelectedConversation,
    lastMapBoundaryBoxCenter,
    setLastMapBoundaryBoxCenter,
    lastZoomLevel,
    setLastZoomLevel,
    showRemoveListingDialog,
    setShowRemoveListingDialog,
    sharedEventId,
    setSharedEventId,
}: LandingPageProps) => {
    const { i18n } = useTranslation();
    const language = i18n.language;
    const [showJaakkoSpeechBubble, setShowJaakkoSpeechBubble] = useState(false);
    const jaakkoInfoRef = useRef<HTMLButtonElement>(null);
    const speechBubbleRef = useRef<HTMLDivElement>(null);
    const [isIframeLoading, setIsIframeLoading] = useState(true);
    const [showCouldSellModal, setShowCouldSellModal] = useState(false);
    const [showCouldBuyModal, setShowCouldBuyModal] = useState(false);
    const [showBuildingModalTriggeredFromCouldBuyOrCouldSellModal, setShowBuildingModalTriggeredFromCouldBuyOrCouldSellModal] = useState<OffMarketToastMessage | null>(null);
    const [feedPagination, setFeedPagination] = useState({
        offset: 0,
        limit: 5,
        total: 0,
        hasMore: true
    });
    const [feedEvents, setFeedEvents] = useState<Comment[]>([]);
    const [isLoadingFeed, setIsLoadingFeed] = useState(false);

    // Footer scroll states
    const [isFooterHidden, setIsFooterHidden] = useState(false);
    const [isHeaderHidden, setIsHeaderHidden] = useState(false);
    const lastScrollY = useRef(0);
    const scrollTimeout = useRef<NodeJS.Timeout | null>(null);
    const [showLandingPageFeed, setShowLandingPageFeed] = useState(false);
    const [showLandingPageMap, setShowLandingPageMap] = useState(false);
    const [isLoadingMoreLandingPage, setIsLoadingMoreLandingPage] = useState(false);
    const [showFeedbackModal, setShowFeedbackModal] = useState(false);

    const handleShowLandingPagePropertyMap = useCallback(() => {
        // TODO: A more elegant solution needed, now feed is polled on mobile before it is shown.
        // Show feed is by default true, but it is only used on desktop.
        // No need to keep polling on mobile if feed is not shown.
        setShowFeed(false);
        setFeedViewActive(false);
        setShowLandingPageMap(true);
    }, [setShowFeed, setFeedViewActive, setShowLandingPageMap]);

    const handleShowSignupLoginModalLandingPage = useCallback(() => {
        setLoginModalVisible(true);
        TagManager.dataLayer({
            dataLayer: {
                event: 'open_signup_login_modal_landing_page',
                category: 'User Interaction',
                action: 'Click',
                label: 'Open Signup Login Modal Landing Page',
            },
        });
    }, [setLoginModalVisible]);

    const fetchFeed = useCallback(async (loadMore = false) => {
        try {
            const offset = loadMore ? feedPagination.offset + feedPagination.limit : 0;
            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/feed?country=${countryOfFeedContent}&offset=${offset}&limit=${feedPagination.limit}`,
                {
                    method: 'GET',
                    credentials: 'include',
                    headers: {
                        'X-CSRF-TOKEN': getCookie('csrf_access_token'),
                    },
                });

            // Check if the response is ok before parsing
            if (!response.ok) {
                console.error('Feed API error:', response.status, response.statusText);
                // Don't update state with undefined values
                return;
            }

            const data = await response.json();

            // Ensure data has the expected structure
            if (!data || !data.items || !data.pagination) {
                console.error('Invalid feed response structure:', data);
                return;
            }

            if (loadMore) {
                setFeedEvents(prev => [...prev, ...data.items]);
            } else {
                setFeedEvents(data.items);
            }

            setFeedPagination({
                offset: loadMore ? feedPagination.offset + feedPagination.limit : 0,
                limit: data.pagination.limit,
                total: data.pagination.total,
                hasMore: data.pagination.has_more
            });
        } catch (error) {
            console.error('Error fetching comments:', error);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [countryOfFeedContent, feedPagination.limit]);

    const handleLoadMoreFeedLandingPage = useCallback(async () => {
        if (!feedPagination.hasMore || isLoadingMore) return;

        setIsLoadingMoreLandingPage(true);
        try {
            const offset = feedPagination.offset + feedPagination.limit;
            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/feed?country=${countryOfFeedContent}&offset=${offset}&limit=${feedPagination.limit}`,
                {
                    method: 'GET',
                    credentials: 'include',
                    headers: {
                        'X-CSRF-TOKEN': getCookie('csrf_access_token'),
                    },
                });

            // Check if the response is ok before parsing
            if (!response.ok) {
                console.error('Load more feed API error:', response.status, response.statusText);
                return;
            }

            const data = await response.json();

            // Ensure data has the expected structure
            if (!data) {
                console.error('Invalid load more feed response:', data);
                return;
            }

            // Only update state if we actually got new items
            if (data.items && data.items.length > 0) {
                setFeedEvents(prev => {
                    const existingCommentsMap = new Map(
                        prev.map(comment => [comment._id, comment])
                    );

                    data.items.forEach(newItem => {
                        const existingItem = existingCommentsMap.get(newItem._id);

                        if (!existingItem || new Date(newItem.timestamp) > new Date(existingItem.timestamp)) {
                            existingCommentsMap.set(newItem._id, newItem);
                        }
                    });

                    return Array.from(existingCommentsMap.values());
                });

                setFeedPagination({
                    offset: offset,
                    limit: data.pagination.limit,
                    total: data.pagination.total,
                    hasMore: data.pagination.has_more
                });
            } else if (data.pagination && !data.pagination.has_more) {
                // Handle case with no more items
                setFeedPagination(prev => ({
                    ...prev,
                    hasMore: false
                }));
            }
        } catch (error) {
            console.error('Error loading more feed items:', error);
        } finally {
            setIsLoadingMoreLandingPage(false);
        }
    }, [feedPagination.hasMore, feedPagination.limit, feedPagination.offset, countryOfFeedContent, isLoadingMore]);

    useEffect(() => {
        if (showFeed || !showBuildingModal) {
            setIsLoadingFeed(true);
            // Use an async IIFE to allow await
            (async () => {
                await fetchFeed(false);
                setIsLoadingFeed(false);
            })();
        }
    }, [showFeed, fetchFeed, showBuildingModal]);

    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (
                speechBubbleRef.current &&
                !speechBubbleRef.current.contains(event.target as Node) &&
                jaakkoInfoRef.current &&
                !jaakkoInfoRef.current.contains(event.target as Node)
            ) {
                setShowJaakkoSpeechBubble(false);
            }
        }

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [speechBubbleRef, jaakkoInfoRef]);

    // Footer/header scroll detection - now uses window scroll
    useEffect(() => {
        const handleScroll = () => {
            // Don't hide footer/header if any modal/overlay is open
            if (showBuildingModal || showExploreListings || messagesModalVisible ||
                loginModalVisible || showCouldSellModal || showCouldBuyModal ||
                showLandingPageFeed || showLandingPageMap) {
                setIsFooterHidden(false);
                setIsHeaderHidden(false);
                return;
            }


            const currentScrollY = window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0;
            const scrollDelta = currentScrollY - lastScrollY.current;

            if (scrollTimeout.current) {
                clearTimeout(scrollTimeout.current);
            }

            // Only hide/show if scroll delta is significant
            if (Math.abs(scrollDelta) > 5) {
                if (scrollDelta > 0 && currentScrollY > 100) {
                    // Scrolling down and not at the top
                    setIsFooterHidden(true);
                    setIsHeaderHidden(true);
                } else if (scrollDelta < 0 || currentScrollY <= 100) {
                    // Scrolling up or near the top
                    setIsFooterHidden(false);
                    setIsHeaderHidden(false);
                }
            }

            lastScrollY.current = currentScrollY;
        };

        // Listen to window scroll since that's where scrolling happens now
        window.addEventListener('scroll', handleScroll, { passive: true });

        const currentScrollTimeout = scrollTimeout.current;
        return () => {
            window.removeEventListener('scroll', handleScroll);
            if (currentScrollTimeout) {
                clearTimeout(currentScrollTimeout);
            }
        };
    }, [showBuildingModal, showExploreListings, messagesModalVisible,
        loginModalVisible, showCouldSellModal, showCouldBuyModal,
        showLandingPageFeed, showLandingPageMap]);

    return (
        <div
            className={`landing-page ${isHeaderHidden || showFeedbackModal ? 'landing-page--header-hidden' : ''}`}
        >
            <div
                className={`header ${isHeaderHidden || showFeedbackModal ? 'header--hidden' : ''}`}
            >
                <button
                    className="header__section__first-row-button"
                    onClick={() => {
                        TagManager.dataLayer({
                            dataLayer: {
                                event: 'could_sell_landing_page_clicked',
                                category: 'User Interaction',
                                action: 'Click',
                                label: 'Could Sell Landing Page Clicked',
                            },
                        });
                        TagManager.dataLayer({
                            dataLayer: {
                              event: 'off_market_could_sell',
                              category: 'User Interaction',
                              action: 'Click',
                              label: 'Off Market Could Sell'
                            },
                          });
                        setShowCouldSellModal(true);
                    }}
                >
                    <OffMarketSellIcon />
                    <Trans i18nKey="building.offMarket.couldSell" />
                </button>
                <button
                    className="header__section__first-row-button"
                    onClick={() => {
                        TagManager.dataLayer({
                            dataLayer: {
                                event: 'could_buy_landing_page_clicked',
                                category: 'User Interaction',
                                action: 'Click',
                                label: 'Could Buy Landing Page Clicked',
                            },
                        });
                        TagManager.dataLayer({
                            dataLayer: {
                              event: 'off_market_could_buy',
                              category: 'User Interaction',
                              action: 'Click',
                              label: 'Off Market Could Buy'
                            },
                          });
                        setShowCouldBuyModal(true);
                    }}
                >
                    <OffMarketBuyIcon />
                    <Trans i18nKey="building.offMarket.couldBuy" />
                </button>
{/*                 <button
                    className="header__section__first-row-button"
                    onClick={() => {
                        TagManager.dataLayer({
                            dataLayer: {
                                event: 'map_landing_page_clicked',
                                category: 'User Interaction',
                                action: 'Click',
                                label: 'Map Landing Page Clicked',
                            },
                        });
                        handleShowLandingPagePropertyMap();
                    }}
                >
                    <RealEstateMapIcon />
                    <Trans i18nKey="landingPage.map.title" />
                </button> */}
                <button
                    className="header__section__start-search-here-button"
                    onClick={() => {
                        TagManager.dataLayer({
                            dataLayer: {
                                event: 'start_search_landing_page_clicked',
                                category: 'User Interaction',
                                action: 'Click',
                                label: 'Start Search Landing Page Clicked',
                            },
                        });
                        handleShowExploreListings();
                    }}
                >
                    <svg className="header__section__start-search-here-button__search-icon" viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg">
                        <path d="M13 0c7.18 0 13 5.82 13 13 0 2.868-.929 5.519-2.502 7.669l7.916 7.917-2.828 2.828-7.917-7.916A12.942 12.942 0 0 1 13 26C5.82 26 0 20.18 0 13S5.82 0 13 0zm0 4a9 9 0 1 0 0 18 9 9 0 0 0 0-18z" fill="currentColor"></path>
                    </svg>
                    <Trans i18nKey="landingPage.startSearch" />
                </button>
            </div>
            {showCouldSellModal && (
                <CouldBuyOrCouldSellModal
                    isOpen={showCouldSellModal}
                    onClose={() => setShowCouldSellModal(false)}
                    actionType="sell"
                    isLoading={false}
                    isLoggedIn={isLoggedIn}
                    setLoginModalVisible={setLoginModalVisible}
                    setShowBuildingModalTriggeredFromCouldBuyOrCouldSellModal={setShowBuildingModalTriggeredFromCouldBuyOrCouldSellModal}
                    onSelectBuilding={handleSelectBuildingFromExplore}
                    countryOfFeedContent={countryOfFeedContent}
                    fetchConversations={fetchConversations}
                    fetchFeed={fetchFeed}
                />
            )}
            {showCouldBuyModal && (
                <CouldBuyOrCouldSellModal
                    isOpen={showCouldBuyModal}
                    onClose={() => setShowCouldBuyModal(false)}
                    actionType="buy"
                    isLoading={false}
                    isLoggedIn={isLoggedIn}
                    setLoginModalVisible={setLoginModalVisible}
                    setShowBuildingModalTriggeredFromCouldBuyOrCouldSellModal={setShowBuildingModalTriggeredFromCouldBuyOrCouldSellModal}
                    onSelectBuilding={handleSelectBuildingFromExplore}
                    countryOfFeedContent={countryOfFeedContent}
                    fetchConversations={fetchConversations}
                    fetchFeed={fetchFeed}
                />
            )}
{/*             {showLandingPageFeed && (
                <div
                    className="landing-page-feed"
                >
                    <div className="landing-page-feed__header">
                        <div className="landing-page-feed__header__title">
                            <Trans i18nKey="feed.title" />
                        </div>
                        <button
                            className="landing-page-feed__header__close-button"
                            onClick={() => {
                                setShowLandingPageFeed(false);
                            }}
                        >
                            <CloseIcon size={24} color="#000" />
                        </button>
                    </div>
                    <FeedEvents
                        isLandingPage
                        isLoadingFeed={isLoadingFeed}
                        feedComments={feedComments}
                        handleFeedCommentClick={handleFeedCommentClick}
                        handleRefreshFeedClick={handleRefreshFeedClick}
                        isRefreshingFeed={isRefreshingFeed || !countryOfFeedContent}
                        isLoggedIn={isLoggedIn}
                        setLoginModalVisible={setLoginModalVisible}
                        handleLoadMoreFeed={handleLoadMoreFeed}
                        isLoadingMore={isLoadingMore}
                        hasMoreFeed={feedPagination.hasMore}
                        fetchFeed={fetchFeed}
                        setFeedComments={setFeedComments}
                    />
                </div>
            )} */}
            <div className="landing-page-content-wrapper">
                {!showLandingPageMap && (
                    <LandingPagePropertyMap
                        selectedBuilding={selectedBuilding}
                        selectBuilding={selectBuilding}
                        userLocation={mapCenter}
                        handleShowLandingPagePropertyMap={handleShowLandingPagePropertyMap}
                    />
                )}
                <LandingPageFeedEvents
                    isLandingPage
                    isLoadingFeed={isLoadingFeed}
                    feedEvents={feedEvents}
                    handleFeedCommentClick={handleFeedCommentClick}
                    handleRefreshFeedClick={handleRefreshFeedClick}
                    isRefreshingFeed={isRefreshingFeed || !countryOfFeedContent}
                    isLoggedIn={isLoggedIn}
                    setLoginModalVisible={setLoginModalVisible}
                    handleLoadMoreFeed={handleLoadMoreFeedLandingPage}
                    isLoadingMore={isLoadingMoreLandingPage}
                    hasMoreFeed={feedPagination.hasMore}
                    fetchFeed={fetchFeed}
                    setFeedEvents={setFeedEvents}
                    setShowLandingPageFeed={setShowLandingPageFeed}
                    countryOfFeedContent={countryOfFeedContent}
                    feedPagination={feedPagination}
                    setShowFeedbackModal={setShowFeedbackModal}
                    showFeedbackModal={showFeedbackModal}
                    userEmail={userEmail}
                />
            </div>
            <div
                className={`footer ${isFooterHidden || showFeedbackModal ? 'footer--hidden' : ''}`}
            >
                {showJaakkoSpeechBubble && (
                    <div className="footer__jaakko-speech-bubble" ref={speechBubbleRef}>
                        <iframe
                            title="vimeo-player"
                            src={language.startsWith('fi') ? 'https://player.vimeo.com/video/1068410842' : 'https://player.vimeo.com/video/1068410835'}
                            width="100%"
                            height="100%"
                            frameBorder="0"
                            allowFullScreen
                            // When the iframe finishes loading, mark it loaded
                            onLoad={() => setIsIframeLoading(false)}
                            style={{ display: isIframeLoading ? 'none' : 'block' }}
                        ></iframe>

                        {/* Conditionally show your loading indicator */}
                        {isIframeLoading && (
                            <div className="loading-indicator">
                                <LoadingSpinner />
                            </div>
                        )}
                    </div>
                )}
                <button
                    className="footer__section__first-row-button"
                    onClick={() => {
                        setShowJaakkoSpeechBubble(!showJaakkoSpeechBubble);
                        TagManager.dataLayer({
                            dataLayer: {
                                event: 'jaakko_info_landing_page_clicked',
                                category: 'User Interaction',
                                action: 'Click',
                                label: 'Jaakko Info Landing Page Clicked',
                            },
                        });
                    }}
                    ref={jaakkoInfoRef}
                >
                    <img
                        src="/assets/info_jaakko.png"
                        alt="Jaakko info"
                        height="36px"
                    />
                    Jaakko info
                </button>
                <button
                    className="footer__section__first-row-button"
                    onClick={() => {
                        TagManager.dataLayer({
                            dataLayer: {
                                event: 'messages_landing_page_clicked',
                                category: 'User Interaction',
                                action: 'Click',
                                label: 'Messages Landing Page Clicked',
                            },
                        });
                        if (!isLoggedIn) {
                            setLoginModalVisible(true);
                        } else {
                            toggleMessagesModal();
                        }
                    }}
                >
                    <MessagesIcon
                        height={29}
                        width={29}
                        newMessagesCount={newMessagesCount}
                        className="footer__section__first-row-button__messages-icon"
                    />
                    <Trans i18nKey="navbar.messages" />
                </button>
                <button
                    className="footer__section__first-row-button"
                    onClick={handleShowSignupLoginModalLandingPage}
                >
                    <UserIcon
                        height={25}
                        width={25}
                        className="footer__section__first-row-button__user-icon"
                    />
                    {isLoggedIn ? (
                        <Trans i18nKey="navbar.loggedIn" />
                    ) : (
                        <Trans i18nKey="navbar.login" />
                    )}
                </button>
            </div>
            {showExploreListings && (
                <div className={`explore-listings-overlay ${showBuildingModal ? 'explore-listings-overlay--building-view-open' : ''}`}>
                    <ExploreListings
                        onClose={() => {
                            setShowExploreListings(false);
                        }}
                        isLoggedIn={isLoggedIn}
                        setLoginModalVisible={setLoginModalVisible}
                        onSelectBuilding={handleSelectBuildingFromExplore}
                        showFilters={showFilters}
                        setShowFilters={setShowFilters}
                        countryOfFeedContent={countryOfFeedContent}
                        setShowRemoveListingDialog={setShowRemoveListingDialog}
                        showRemoveListingDialog={showRemoveListingDialog}
                        fetchFeed={fetchFeed}
                        handleRefreshFeedClick={handleRefreshFeedClick}
                        setFocusToLocationInput
                    />
                </div>
            )}
            {selectedBuilding && showBuildingModal && (
                <BuildingModalGlobal
                    isLandingPage
                    isLoggedIn={isLoggedIn}
                    selectedBuilding={selectedBuilding}
                    showImageModal={showImageModal}
                    countryOfFeedContent={countryOfFeedContent}
                    messagesModalVisible={messagesModalVisible}
                    showBuildingModalTriggeredFromCouldBuyOrCouldSellModal={showBuildingModalTriggeredFromCouldBuyOrCouldSellModal}
                    sharedEventId={sharedEventId}
                    setSharedEventId={setSharedEventId}
                    toggleShowBuildingModal={toggleShowBuildingModal}
                    setSelectedBuilding={setSelectedBuilding}
                    setLoginModalVisible={setLoginModalVisible}
                    refreshFeed={handleRefreshFeedClick}
                    setShowImageModal={setShowImageModal}
                    setCountryOfFeedContent={setCountryOfFeedContent}
                    fetchConversations={fetchConversations}
                    setShowBuildingModalTriggeredFromCouldBuyOrCouldSellModal={setShowBuildingModalTriggeredFromCouldBuyOrCouldSellModal}
                />
            )}
            {isLoggedIn && messagesModalVisible && (
                <MessagesModal
                    isLandingPage
                    isVisible={messagesModalVisible}
                    toggleMessagesModal={toggleMessagesModal}
                    conversations={conversations}
                    selectedConversation={selectedConversation}
                    setSelectedConversation={setSelectedConversation}
                    refreshConversations={fetchConversations}
                />
            )}
            {loginModalVisible && (
                <SignupLoginModal
                    toggleSignupLoginModalVisible={() => setLoginModalVisible(false)}
                    setIsLoggedIn={setIsLoggedIn}
                    isLoggedIn={isLoggedIn}
                    onLoginSuccess={handleLoginSuccess}
                    isLandingPage
                />
            )}
            {(showExploreListings || showLandingPageFeed) && (!selectedBuilding && !showLandingPageMap && !showFilters && !showRemoveListingDialog) && (
                <button
                    className={`map-feed-toggle-button`}
                    onClick={() => {
                        TagManager.dataLayer({
                            dataLayer: {
                                event: `show_map_clicked_in_new_${showLandingPageFeed ? 'feed' : 'explore_listings'}`,
                                category: 'User Interaction',
                                action: 'Click',
                                label: `Show Map Clicked in New ${showLandingPageFeed ? 'Feed' : 'Explore Listings'}`,
                            },
                        });
                        handleShowLandingPagePropertyMap();
                    }}>
                    <>
                        <div className="map-feed-toggle-button__map-text">
                            <Trans i18nKey="map.showMap" />
                        </div>
                        <MapIcon />
                    </>
                </button>
            )}
            {showLandingPageMap && (
                <div
                    className="landing-page-map"
                >
                    <div className="landing-page-map__header">
                        <div className="landing-page-map__header__title">
                            <Trans i18nKey="landingPage.map.title" />
                        </div>
                        <button
                            className="landing-page-map__header__close-button"
                            onClick={() => setShowLandingPageMap(false)}
                        >
                            <CloseIcon size={24} color="#000" />
                        </button>
                    </div>
                    <SearchInput
                        setMapCenter={setMapCenter}
                        setTriggeredFromSearch={setTriggeredFromSearch}
                        setCountryOfFeedContent={setCountryOfFeedContent}
                        countryOfFeedContent={countryOfFeedContent}
                        feedHidden={!showFeed}
                        isLandingPage
                    />
                    <BuildingMap
                        isLandingPage
                        feedIsOpen={false}
                        mobileFeedIsOpen={false}
                        mapCenter={mapCenter}
                        selectedBuilding={selectedBuilding}
                        triggeredFromSearch={triggeredFromSearch}
                        countryOfFeedContent={countryOfFeedContent}
                        lastMapBoundaryBoxCenter={lastMapBoundaryBoxCenter}
                        lastZoomLevel={lastZoomLevel}
                        initialMapZoom={initialMapZoom}
                        setShowFeed={setShowFeed}
                        selectBuilding={selectBuilding}
                        setTriggeredFromSearch={setTriggeredFromSearch}
                        setCountryOfFeedContent={setCountryOfFeedContent}
                        setLastMapBoundaryBoxCenter={setLastMapBoundaryBoxCenter}
                        setLastZoomLevel={setLastZoomLevel}
                    />
                </div>
            )}
        </div>
    );
};

export default LandingPage;
